
<template lang="html">
    <report
        v-if="hasData"
        id="watershed-report"
        class="watershed web report"
        title="Watershed Summary"
        :subtitle="watershedName"
        :report-data="reportData"
        :sections="sections"
        :buttons="buttons"
        :removed-sections="removedSections"
        @download-pdf="downloadPdf"
    >
        <template v-slot:header-content>
            <bus-stops
                class="bus-stops"
                :stops="reportData.overview.busStopNames"
            />
        </template>
    </report>
</template>

<script>
import BusStops from '@foundryspatial/bus-stops';
import Report from '@foundryspatial/report';
import download from 'downloadjs';

import AllocationsByIndustrySection from '@/components/WatershedReport/AllocationsByIndustrySection';
import AllocationsTableSection from '@/components/WatershedReport/AllocationsTableSection';
import AnnualHydrologySection from '@/components/WatershedReport/AnnualHydrologySection';
import ClimateSection from '@/components/WatershedReport/ClimateSection';
import HydrologicVariabilitySection from '@/components/WatershedReport/HydrologicVariabilitySection';
import IntroductionSection from '@/components/WatershedReport/IntroductionSection';
import LandcoverSection from '@/components/WatershedReport/LandcoverSection';
import MethodsSection from '@/components/WatershedReport/MethodsSection';
import MonthlyHydrologySection from '@/components/WatershedReport/MonthlyHydrologySection';
import NotesSection from '@/components/WatershedReport/NotesSection';
import OverviewSection from '@/components/WatershedReport/OverviewSection';
import ReferencesSection from '@/components/WatershedReport/ReferencesSection';
import TopographySection from '@/components/WatershedReport/TopographySection';
import {
    API_ERROR_WATERSHED_DATA,
    API_ERROR_WATERSHED_PDF,
    PDF_SUCCESSFUL_DOWNLOAD,
} from '@/constants/messages';

export default {
    name: 'WatershedReport',

    components: {
        BusStops,
        Report,
    },

    props: {
        // click location as a mapbox.lngLat object
        lngLat: {
            type: Object,
            required: true,
        },

        // selected WFI
        wfi: {
            type: String,
            required: true,
        },

        // name of the selected watershed
        watershedName: {
            type: String,
            required: true,
        },

        fwa: {
            type: String,
            required: true,
        },
    },

    data: () => ({
        hasData: false,
        reportData: null,
        activeId: null,
        sections: [
            {
                title: 'Overview',
                id: 'overview',
                sectionComponent: OverviewSection,
            },
            {
                title: 'Introduction',
                id: 'introduction',
                sectionComponent: IntroductionSection,
            },
            {
                title: 'Annual Hydrology',
                id: 'annual-hydrology',
                sectionComponent: AnnualHydrologySection,
            },
            {
                title: 'Monthly Hydrology',
                id: 'monthly-hydrology',
                sectionComponent: MonthlyHydrologySection,
            },
            {
                title: 'Allocations By Industry',
                id: 'allocations-by-industry',
                sectionComponent: AllocationsByIndustrySection,
            },
            {
                title: 'Allocations',
                id: 'allocations-table',
                sectionComponent: AllocationsTableSection,
            },
            {
                title: 'Hydrologic Variability',
                id: 'hydrologic-variability',
                sectionComponent: HydrologicVariabilitySection,
            },
            {
                title: 'Landcover',
                id: 'landcover',
                sectionComponent: LandcoverSection,
            },
            {
                title: 'Climate',
                id: 'climate',
                sectionComponent: ClimateSection,
            },
            {
                title: 'Topography',
                id: 'topography',
                sectionComponent: TopographySection,
            },
            {
                title: 'Notes',
                id: 'notes',
                sectionComponent: NotesSection,
            },
            {
                title: 'References',
                id: 'references',
                sectionComponent: ReferencesSection,
            },
            {
                title: 'Methods',
                id: 'methods',
                sectionComponent: MethodsSection,
            },
        ],
        hiddenOnSmallScreens: [
            'allocations-table',
        ],
        pdfLoading: false,
    }),

    computed: {
        removedSections() {
            const removedSections = [];

            // conditionally add section id's to removedSections array
            if (!this.reportData.hydrologicVariability) { removedSections.push('hydrologic-variability'); }

            return removedSections;
        },

        buttons() {
            return [
                {
                    label: 'Download PDF',
                    event: 'download-pdf',
                    loading: this.pdfLoading,
                },
            ];
        },
    },

    mounted() {
        this.getData();
    },

    methods: {
        /**
         * fetch all required report data from the API
         */
        async getData() {
            try {
                // load the watershed report information
                const apiResponse = await this.$api.getWatershedReport(
                    this.lngLat,
                    this.wfi,
                    this.watershedName,
                    this.fwa,
                );

                this.reportData = {
                    ...apiResponse,
                    // add click location to reportData
                    lngLat: this.lngLat,
                };

                window.gtag('event', 'Report', { action: 'watershed-click', label: this.wfi });

                this.hasData = true;
                this.$emit('report-data-available');
            } catch (e) {
                this.$emit('report-data-error');
                this.$handleError(e, API_ERROR_WATERSHED_DATA);
            }
        },

        /**
         * request a PDF version of the StaticReport component from the API
         * and trigger a download in the browser
         */
        async downloadPdf() {
            try {
                this.pdfLoading = true;
                const apiResponse = await this.$api.getWatershedReportPdf(
                    this.lngLat,
                    this.wfi,
                    this.watershedName,
                    this.fwa,
                );
                download(apiResponse, `watershed_${this.wfi}.pdf`);
                this.$buefy.snackbar.open({
                    message: PDF_SUCCESSFUL_DOWNLOAD,
                    type: 'is-success',
                });
                window.gtag('event', 'PDF', { action: 'pdf-download', label: this.wfi });
            } catch (e) {
                this.$handleError(e, API_ERROR_WATERSHED_PDF);
            } finally {
                this.pdfLoading = false;
            }
        },
    },
};
</script>

<style lang="scss">
#watershed-report {
    .bus-stops {
        height: 6rem;
    }
}
</style>
